import LoginAreaLinks from "./LoginAreaLinks";
import ThemeToggle from "./ThemeToggle";
import { isNativeFamilyApp } from "./PWAInstallPrompt";
import { isNativeOssApp } from "../lib/oss/bridge";
import { isNativeSanitarioApp } from "../lib/sanitario/bridge";
import { isNativeVisiteApp } from "../lib/visite/bridge";

const ACCENTS = {
  emerald: {
    page: "bg-[#f8fbfa]",
    aside: "from-[#f3faf8] via-white to-[#eef8f4]",
    blobA: "bg-emerald-200/40",
    blobB: "bg-emerald-100/70",
    blobC: "bg-[#d7f1e8]/70",
    featureWrap: "bg-emerald-100/80 text-emerald-700",
    button: "bg-emerald-600 hover:bg-emerald-700",
    ring: "focus-visible:ring-emerald-500/30",
    theme: "emerald",
  },
  sky: {
    page: "bg-[#f5f9fc]",
    aside: "from-[#eef6fb] via-white to-[#e8f4fb]",
    blobA: "bg-sky-200/45",
    blobB: "bg-sky-100/80",
    blobC: "bg-sky-50/90",
    featureWrap: "bg-sky-100/80 text-sky-800",
    button: "bg-sky-600 hover:bg-sky-700",
    ring: "focus-visible:ring-sky-500/30",
    theme: "sky",
  },
  indigo: {
    page: "bg-[#f6f5fb]",
    aside: "from-[#f3f0fb] via-white to-[#eceaf8]",
    blobA: "bg-indigo-200/40",
    blobB: "bg-indigo-100/75",
    blobC: "bg-violet-50/90",
    featureWrap: "bg-indigo-100/80 text-indigo-800",
    button: "bg-indigo-600 hover:bg-indigo-700",
    ring: "focus-visible:ring-indigo-500/30",
    theme: "indigo",
  },
  orange: {
    page: "bg-[#fbf8f5]",
    aside: "from-[#fbf4ec] via-white to-[#f8eee4]",
    blobA: "bg-orange-200/40",
    blobB: "bg-orange-100/70",
    blobC: "bg-amber-50/90",
    featureWrap: "bg-orange-100/80 text-orange-800",
    button: "bg-orange-500 hover:bg-orange-600",
    ring: "focus-visible:ring-orange-500/30",
    theme: "orange",
  },
  teal: {
    page: "bg-[#f4fbfb]",
    aside: "from-[#eef8f8] via-white to-[#e6f4f4]",
    blobA: "bg-teal-200/40",
    blobB: "bg-cyan-100/70",
    blobC: "bg-teal-50/90",
    featureWrap: "bg-teal-100/80 text-teal-800",
    button: "bg-teal-600 hover:bg-teal-700",
    ring: "focus-visible:ring-teal-500/30",
    theme: "teal",
  },
  blue: {
    page: "bg-[#f4f6fd]",
    aside: "from-[#eef2fc] via-white to-[#e8edfa]",
    blobA: "bg-blue-200/40",
    blobB: "bg-blue-100/70",
    blobC: "bg-sky-50/90",
    featureWrap: "bg-blue-100/80 text-blue-800",
    button: "bg-[#4068E8] hover:bg-[#3557D4]",
    ring: "focus-visible:ring-[#4068E8]/30",
    theme: "blue",
  },
  navy: {
    page: "bg-[#f5f7fa]",
    aside: "from-[#eef2f7] via-white to-[#e8eef5]",
    blobA: "bg-slate-300/35",
    blobB: "bg-sky-100/70",
    blobC: "bg-slate-100/90",
    featureWrap: "bg-slate-200/80 text-[#1e3a5f]",
    button: "bg-[#1e3a5f] hover:bg-[#162d4a]",
    ring: "focus-visible:ring-slate-400/40",
    theme: "sky",
  },
  violet: {
    page: "bg-[#f7f5fb]",
    aside: "from-[#f4eefb] via-white to-[#eee8f7]",
    blobA: "bg-[#6e48b5]/25",
    blobB: "bg-[#ede9f6]",
    blobC: "bg-[#e4dcf4]/80",
    featureWrap: "bg-[#ede9f6] text-[#6e48b5]",
    button: "bg-[#6e48b5] hover:bg-[#5a3b94]",
    ring: "focus-visible:ring-[#6e48b5]/30",
    theme: "violet",
  },
  rose: {
    page: "bg-[#fdf6f7]",
    aside: "from-[#fceef0] via-white to-[#f8e6e9]",
    blobA: "bg-rose-200/45",
    blobB: "bg-rose-100/75",
    blobC: "bg-rose-50/90",
    featureWrap: "bg-rose-100/80 text-rose-800",
    button: "bg-[#C83048] hover:bg-[#A8283C]",
    ring: "focus-visible:ring-[#C83048]/30",
    theme: "rose",
  },
};

export default function AreaLoginShell({
  accent = "emerald",
  logo,
  title,
  subtitle,
  features = [],
  currentArea,
  children,
  footer,
  // Opzionali (usati da Drive per WebView/PWA): classi extra e nascondere i link alle altre aree.
  toggleClassName = "fixed right-3 top-3 z-50",
  mainClassName = "",
  hideAreaLinks = false,
  themeControl = null, // sostituisce il pulsante sole/luna (Drive: Sistema/Chiaro/Scuro)
}) {
  const styles = ACCENTS[accent] || ACCENTS.emerald;

  return (
    <div className={`min-h-screen overflow-hidden text-slate-900 ${styles.page}`}>
      {themeControl ? <div className={toggleClassName}>{themeControl}</div> : <ThemeToggle className={toggleClassName} testId="theme-toggle-login" />}
      <div className="grid min-h-screen lg:grid-cols-[0.9fr_1.1fr]">
        <aside
          className={`relative hidden overflow-hidden bg-gradient-to-br px-10 py-10 lg:flex lg:flex-col ${styles.aside}`}
        >
          <div className="relative z-10 max-w-md">
            {logo}
            <h1 className="text-[2rem] font-semibold leading-tight tracking-tight text-slate-900">
              {title}
            </h1>
            {subtitle ? (
              <p className="mt-4 text-sm leading-6 text-slate-600">{subtitle}</p>
            ) : null}

            {features.length > 0 ? (
              <div className="mt-9 space-y-5">
                {features.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="flex gap-4">
                      <span
                        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${styles.featureWrap}`}
                      >
                        <Icon className="h-5 w-5" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-slate-900">{item.title}</span>
                        <span className="mt-0.5 block text-xs leading-5 text-slate-500">{item.text}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className={`absolute -bottom-24 -left-28 h-72 w-[32rem] rounded-[55%] blur-2xl ${styles.blobA}`} />
          <div className={`absolute -bottom-16 left-0 h-48 w-[115%] rounded-[55%_45%_0_0] ${styles.blobB}`} />
          <div className={`absolute bottom-[-2rem] right-[-18%] h-52 w-[78%] rounded-tl-[100%] ${styles.blobC}`} />
        </aside>

        <main className={`flex min-h-screen items-center justify-center px-4 py-6 sm:px-6 lg:px-8 ${mainClassName}`}>
          <div className="w-full max-w-[440px]">
            <div className="mb-5 text-center lg:hidden">
              {logo}
            </div>

            <section className="rounded-[1.6rem] border border-white/80 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.08)] sm:p-7">
              {children}
              {!isNativeFamilyApp() && !isNativeOssApp() && !isNativeSanitarioApp() && !isNativeVisiteApp() && !hideAreaLinks ? (
                <LoginAreaLinks current={currentArea} theme={styles.theme} className="mt-6" />
              ) : null}
            </section>

            {footer}
          </div>
        </main>
      </div>
    </div>
  );
}

export { ACCENTS };
